<template>
    <div class="rich-video-message-container">
        <div class="header-message">
            <p>Rich Video ({{ templateOrder + 1 }}/3)</p>
            <div @click="removeRichVideoTemplate">
                <i class="bx bx-trash"></i>
            </div>
        </div>
        <div class="message-content">
          <div class="input-group">
            <label class="input-label" for="item-name">
                Item name
                <b-tooltip
                  label="The item name is shown in push notifications and in the user's chat list."
                  position="is-right"
                  class="tooltip-container"
                  type="is-dark"
                  multilined
                >
                  <div label="Right">
                    <i class='bx bx-info-circle'></i>
                  </div>
                </b-tooltip>
            </label>
            <ValidationProvider
              :rules="{
                required: {
                  value: true,
                  message: 'Please enter an item name',
                },
                max: {
                  value: 400
                }
              }"
              v-slot="{ errors }"
            >
              <input
                type="text"
                id="item-name"
                class="item-name-input"
                placeholder="Enter item name"
                @input="updateValueFromTextInput('itemName', $event.target.value)"
                :value="textInputValue.itemName"
              />
              <div class="error-container">
                <p class="error-message">{{ errors[0] }}</p>
              </div>
            </ValidationProvider>
          </div>
          <div class="video-dimension-dropdown" ref="videoDimensionDropdownRef">
            <label class="input-label">
                Video size
            </label>
            <div class="item-selected" @click="onClickVideoDimensionDropdownBox">
                <p class="selected-option-text">{{ selectedVideoDimension.label }}</p>
                <i class="bx bx-caret-up" v-if="isVideoDimensionDropdownBoxClicked"></i>
                <i class="bx bx-caret-down" v-else></i>
            </div>
            <div class="item-dropdown" v-if="isVideoDimensionDropdownBoxClicked">
                <div
                  v-for="option in videoDimensionDropdownOption"
                  :key="option.value"
                  @click="onClickVideoDimensionDropdownOption(option.value)"
                  class="item-option"
                  :class="{'selected-item': option.value === selectedVideoDimension.value}"
                  >
                    <p>{{ option.label }}</p>
                </div>
            </div>
            <p class="text-description">The uploaded video must match the selected size.</p>
          </div>
          <div class="video-and-action-container">
            <div>
              <ValidationProvider
                :value="dataURL"
                :rules="isVideoFromCopiedMessage ? null : {
                  required: {
                    value: true,
                    message: 'Please upload a video',
                  },
                  multiCustom: {
                    callbacks: [isValidVideoType, isValidVideoSize, checkUploadingVideoDimension],
                    messages: [
                      'The file upload must be in MP4, MOV, or WMV format',
                      'The maximum file upload size is 50 MB',
                      `Please upload a video with dimensions ${selectedVideoDimension.label}`
                    ],
                  }
                }"
                v-slot="{ errors }"
                ref="videoValidationProviderRef"
              >
                <div v-if="isUploadingVideo" class="uploading-container">
                    <div class="loading-section">
                        <div class="spinner-border" role="status"></div>
                    </div>
                </div>
                <div v-else class="message-content video-container">
                    <div v-if="thumbnailVideo === null" class="dropzone-container">
                        <vue-dropzone
                            ref="videoDropzoneRef"
                            id="video-dropzone"
                            :options="videoDropzoneOptions"
                            :useCustomSlot=true
                            @vdropzone-file-added="handleAddedVideo"
                        >
                        <div class="dropzone-custom-input">
                            <div>
                                <i class="bx bx-video"></i>
                            </div>
                            <p class="dropzone-custom-title">Drag an video here
                              <br/>or <span class="text-highlight">Upload a file</span>
                            </p>
                            <p class="text-subtitle">File formats:  MP4, MOV, WMV<br/>File size: Up to 50 MB</p>
                        </div>
                        </vue-dropzone>
                    </div>
                    <div v-else class="thumbnail-container" style="position: relative;">
                        <!-- TODO: Custom video controller UI-->
                        <video
                            :src="thumbnailVideo"
                            controls
                            class="video-container"/>
                        <div
                          @click="removeThumbnailVideo"
                          class="thumbnail-remove"
                        >
                            <i class="bx bx-x" style="color: white; font-size: 16px;"></i>
                        </div>
                    </div>
                    <div class="error-container">
                      <p class="error-message">{{ errors[0] }}</p>
                    </div>
                </div>
              </ValidationProvider>
            </div>
            <div class="action-container">
              <div class="dropzone-container video-thumbnail">
                <p class="input-label video-thumbnail">Video Thumbnail</p>
                <ValidationProvider
                  :value="videoThumbnail"
                  :rules="isThumbnailFromCopiedMessage ? null : {
                    required: {
                      value: true,
                      message: 'Please upload a thumbnail image.',
                    },
                    multiCustom: {
                      callbacks: [isThumbnailTypeValid, isThumbnailSizeValid, checkUploadingThumbnailSize],
                      messages: [
                        'The file upload must be in JPG, JPEG, or PNG format',
                        `Please upload an image with size less than ${maxThumbnailSize} MB.`,
                        `Please upload a thumbnail with ${selectedVideoDimension.label}`
                      ],
                    }
                  }"
                  v-slot="{ errors }"
                  ref="thumbnailValidationProviderRef"
                >
                  <div v-if="isUploadingThumbnail" class="thumbnail-loading-container">
                      <div class="loading-section">
                          <div class="spinner-border" role="status"></div>
                      </div>
                  </div>
                  <div v-else>
                    <div v-if="videoThumbnail === null" class="dropzone-container">
                      <vue-dropzone
                          ref="thumbnailDropzoneRef"
                          id="thumbnail-dropzone"
                          :options="videoThumbnailDropzoneOptions"
                          :useCustomSlot=true
                          @vdropzone-file-added="handleUploadingThumbnail"
                      >
                        <div class="dropzone-custom-input">
                            <i class="bx bx-image"></i>
                        </div>
                      </vue-dropzone>
                    </div>
                    <div v-else class="video-thumbnail-container" style="position: relative;">
                      <img :src="videoThumbnail" alt="thumbnail"/>
                      <div
                        @click="removeThumbnailImage"
                        class="thumbnail-remove"
                      >
                          <i class="bx bx-x" style="color: white; font-size: 16px;"></i>
                      </div>
                    </div>
                  </div>
                  <div class="error-container">
                    <p class="error-message">{{ errors[0] }}</p>
                  </div>
                </ValidationProvider>
              </div>
              <div class="action-dropdown" ref="actionDropdownRef">
                  <label class="input-label action-label">
                    Action Button
                  </label>
                  <div class="item-selected" @click="onClickActionDropdownBox">
                      <p class="selected-option-text">{{ selectedAction.label }}</p>
                      <i class="bx bx-caret-up" v-if="isActionDropdownBoxClicked"></i>
                      <i class="bx bx-caret-down" v-else></i>
                  </div>
                  <div class="item-dropdown" v-if="isActionDropdownBoxClicked">
                    <div
                      v-for="option in actionButtonDropdownOption"
                      :key="option.value"
                      @click="onClickActionDropdownOption(option.value)"
                      class="item-option"
                      :class="{'selected-item': option.value === selectedAction.value}"
                    >
                          <p>{{ option.label }}</p>
                    </div>
                  </div>
              </div>
              <div v-if="selectedAction.value === 'display'">
                <div class="input-group">
                    <label class="input-label" for="link-url-input">
                        Link Url
                    </label>
                    <ValidationProvider
                      :rules="{
                        required: {
                          value: selectedAction.value === 'display',
                          message: 'Please enter a URL',
                        },
                        custom: {
                          callback: isURLValid,
                          message: 'URL must start with https://',
                        },
                        max: {
                          value: 1000,
                        }
                      }"
                      v-slot="{ errors }"
                    >
                      <input
                        id="link-url-input"
                        type="text"
                        placeholder="Enter Url"
                        @input="updateValueFromTextInput('linkUrl', $event.target.value)"
                        class="item-name-input"
                        :value="textInputValue.linkUrl"
                      />
                      <div class="error-container">
                        <p class="error-message">{{ errors[0] }}</p>
                      </div>
                    </ValidationProvider>
                </div>
                <div class="input-group">
                    <label class="input-label" for="action-button-label-input">
                        Action Button label
                        <b-tooltip
                          label="The action button label is the text shown on the button that users can see and interact with."
                          position="is-right"
                          class="tooltip-container text-start"
                          type="is-dark"
                          multilined
                          >
                            <div label="Right">
                              <i class='bx bx-info-circle'></i>
                            </div>
                        </b-tooltip>
                    </label>
                    <ValidationProvider
                      :rules="{
                        required: {
                          value: selectedAction.value === 'display',
                          message: 'Please enter an action button label',
                        },
                        max: {
                          value: 30,
                        }
                      }"
                      v-slot="{ errors }"
                    >
                      <input
                        id="action-button-label-input"
                        type="text"
                        placeholder="Enter Text"
                        @input="updateValueFromTextInput('actionButtonLabel', $event.target.value)"
                        class="item-name-input"
                        :value="textInputValue.actionButtonLabel"
                      />
                      <div class="error-container">
                        <p class="error-message">{{ errors[0] }}</p>
                      </div>
                    </ValidationProvider>
                </div>
              </div>
            </div>
          </div>
        </div>
    </div>
</template>

<script>
import VueDropzone from 'vue2-dropzone';
import Decimal from 'decimal.js';
import { ValidationProvider } from 'vee-validate';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import calculateAspectRatio from '@/utiles/aspectRatioHelper';
import aliceApi from '../../../../alice-api';

const sixteenAsDecimal = new Decimal('16');
const nineAsDecimal = new Decimal('9');
const oneAsDecimal = new Decimal('1');

export default {
  name: 'RichVideoMessage',
  components: {
    VueDropzone,
    ValidationProvider,
  },
  props: {
    templateOrder: {
      type: Number,
      required: true,
    },
    copiedRichVideoMessage: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      videoDropzoneOptions: {
        url: '/',
        acceptedFiles: 'video/mp4,video/mov,video/wmv',
        maxFilesize: 50, // MB
        maxFiles: 1,
        autoProcessQueue: false,
      },
      videoThumbnailDropzoneOptions: {
        url: '/',
        acceptedFiles: 'image/jpeg,image/png',
        maxFilesize: 5, // MB
        maxFiles: 1,
        autoProcessQueue: false,
      },
      thumbnailVideo: null,
      isUploadingVideo: false,
      isVideoDimensionDropdownBoxClicked: false,
      actionButtonDropdownOption: {
        none: { value: 'none', label: 'None' },
        display: { value: 'display', label: 'Display' },
      },
      selectedAction: {
        value: 'none',
        label: 'None',
      },
      isActionDropdownBoxClicked: false,
      dataURL: null,
      isUploadingVideoSizeValid: true,
      textInputValue: {
        itemName: '',
        linkUrl: '',
        actionButtonLabel: '',
      },
      videoDimensionDropdownOption: {
        landscape: {
          value: 'landscape', label: 'Landscape 16:9', width: 1040, height: 585, aspectRatio: sixteenAsDecimal.div(nineAsDecimal).toDecimalPlaces(2)
        },
        square: {
          value: 'square', label: 'Square 1:1', width: 1040, height: 1040, aspectRatio: oneAsDecimal.div(oneAsDecimal).toDecimalPlaces(2)
        },
        portrait: {
          value: 'portrait', label: 'Portrait 9:16', width: 1040, height: 1848, aspectRatio: nineAsDecimal.div(sixteenAsDecimal).toDecimalPlaces(2)
        },
      },
      selectedVideoDimension: {
        value: 'landscape',
        label: 'Landscape 16:9',
      },
      // TODO : this attribute name should be changed in the future
      videoThumbnail: null,
      maxThumbnailSize: 10, // MB
      isUploadingThumbnail: false,
      isVideoFromCopiedMessage: false,
      isThumbnailFromCopiedMessage: false,
    };
  },
  mounted() {
    if (this.copiedRichVideoMessage && Object.keys(this.copiedRichVideoMessage).length > 0) {
      this.loadCopiedRichVideoMessage();
    } else {
      this.$refs.videoValidationProviderRef.validate(this.dataURL);
      this.$refs.thumbnailValidationProviderRef.validate(this.videoThumbnail);
    }
    document.addEventListener('mousedown', this.handleClickOutsideRichVideoDropdowns, true);
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.handleClickOutsideRichVideoDropdowns, true);
  },
  methods: {
    removeRichVideoTemplate() {
      this.thumbnailVideo = null;
      this.videoThumbnail = null;
      this.$emit('remove-message-template');
    },
    isValidVideoType(file) {
      const allowedTypes = ['video/mp4', 'video/mov', 'video/wmv'];
      return allowedTypes.includes(file.type);
    },
    isValidVideoSize(file) {
      const maxSize = 50 * 1024 * 1024;
      return file.size <= maxSize;
    },
    async checkUploadingVideoDimension(file) {
      const dimensions = await this.getVideoDimensions(file);
      const aspectRatio = calculateAspectRatio(dimensions.width, dimensions.height);
      const selectedDimension = this.videoDimensionDropdownOption[this.selectedVideoDimension.value];
      return aspectRatio.equals(selectedDimension.aspectRatio);
    },
    async getVideoDimensions(file) {
      return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
          resolve({ width: video.videoWidth, height: video.videoHeight });
        };
        video.onerror = (error) => {
          reject(error);
        };
        video.src = URL.createObjectURL(file);
      });
    },
    async handleAddedVideo(file) {
      this.isUploadingVideo = true;
      const { valid } = await this.$refs.videoValidationProviderRef.validate(file);
      if (!valid) {
        this.thumbnailVideo = null;
        this.dataURL = null;
        this.isUploadingVideo = false;
        return;
      }
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'VIDEO');
      const response = await this.sendUploadedMedia(formData);
      if (response) {
        this.thumbnailVideo = URL.createObjectURL(file);
        this.dataURL = response.data.data.filePath;
      } else {
        this.dataURL = null;
      }
      this.formatAndEmitRichVideoResponse();
      this.isUploadingVideo = false;
    },
    async sendUploadedMedia(formData) {
      try {
        const response = await aliceApi.uploadMediaMessage(formData);
        return response;
      } catch (error) {
        console.error('Error uploading file:', error);
        return null;
      }
    },
    async removeThumbnailVideo() {
      this.isVideoFromCopiedMessage = false;
      await this.$nextTick();
      this.dataURL = null;
      this.thumbnailVideo = null;
      this.invalidVideoType = null;
      this.invalidVideoDimension = null;
      this.invalidVideoSize = null;
      this.$refs.videoValidationProviderRef.validate(this.dataURL);
      this.formatAndEmitRichVideoResponse();
    },
    onClickActionDropdownBox() {
      this.isActionDropdownBoxClicked = !this.isActionDropdownBoxClicked;
    },
    onClickActionDropdownOption(value) {
      this.selectedAction = this.actionButtonDropdownOption[value];
      this.isActionDropdownBoxClicked = false;
      this.formatAndEmitRichVideoResponse();
    },
    updateValueFromTextInput(field, value) {
      if (field in this.textInputValue) {
        this.textInputValue[field] = value;
      }
      this.formatAndEmitRichVideoResponse();
    },
    formatAndEmitRichVideoResponse() {
      const dimensions = this.videoDimensionDropdownOption[this.selectedVideoDimension.value];
      const responseData = {
        type: 'IMAGEMAP',
        baseUrl: this.videoThumbnail ? `${this.videoThumbnail}?w=auto` : null,
        altText: this.textInputValue.itemName,
        baseSize: {
          width: dimensions.width,
          height: dimensions.height,
        },
        actions: [],
        video: {
          originalContentUrl: this.dataURL,
          previewImageUrl: this.videoThumbnail ? `${this.videoThumbnail}?w=auto` : null,
          area: {
            x: 0,
            y: 0,
            width: dimensions.width,
            height: dimensions.height,
          },
          externalLink: this.selectedAction.value === 'display'
            ? {
              linkUri: this.textInputValue.linkUrl,
              label: this.textInputValue.actionButtonLabel,
            }
            : null,
        },
      };
      this.$emit('update-rich-video-message', responseData);
    },
    loadCopiedRichVideoMessage() {
      this.isVideoFromCopiedMessage = true;
      this.isThumbnailFromCopiedMessage = true;
      this.textInputValue.itemName = this.copiedRichVideoMessage.altText;
      this.selectedAction = this.formatSelectedAction(this.copiedRichVideoMessage.video);
      this.dataURL = this.copiedRichVideoMessage.video.originalContentUrl;
      this.thumbnailVideo = this.copiedRichVideoMessage.video.originalContentUrl;
      this.videoThumbnail = this.copiedRichVideoMessage.video.previewImageUrl;
      this.formatAndEmitRichVideoResponse();
    },
    formatSelectedAction(video) {
      if (!video.externalLink || Object.keys(video.externalLink).length <= 0) {
        return { value: 'none', label: 'None' };
      }
      this.textInputValue.linkUrl = video.externalLink.linkUri;
      this.textInputValue.actionButtonLabel = video.externalLink.label;
      return { value: 'display', label: 'Display' };
    },
    onClickVideoDimensionDropdownBox() {
      this.isVideoDimensionDropdownBoxClicked = !this.isVideoDimensionDropdownBoxClicked;
    },
    async onClickVideoDimensionDropdownOption(value) {
      this.isVideoDimensionDropdownBoxClicked = false;
      if (this.selectedVideoDimension.value === value) return;
      this.selectedVideoDimension = this.videoDimensionDropdownOption[value];
      await this.removeThumbnailVideo();
      await this.removeThumbnailImage();
    },
    // Thumbnail image handling
    async removeThumbnailImage() {
      this.isThumbnailFromCopiedMessage = false;
      await this.$nextTick();
      this.videoThumbnail = null;
      this.$refs.thumbnailValidationProviderRef.validate(this.videoThumbnail);
      this.formatAndEmitRichVideoResponse();
    },
    async handleUploadingThumbnail(file) {
      this.isUploadingThumbnail = true;
      const { valid } = await this.$refs.thumbnailValidationProviderRef.validate(file);
      if (!valid) {
        this.videoThumbnail = null;
        this.isUploadingThumbnail = false;
        return;
      }
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'IMAGE');
      const response = await this.sendUploadedMedia(formData);
      if (response) {
        this.videoThumbnail = response.data.data.filePath;
        this.formatAndEmitRichVideoResponse();
      } else {
        this.videoThumbnail = null;
      }
      this.isUploadingThumbnail = false;
    },
    isThumbnailSizeValid(file) {
      const maxSizeBytes = this.maxThumbnailSize * 1024 * 1024;
      return file.size <= maxSizeBytes;
    },
    isThumbnailTypeValid(file) {
      const acceptedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      return acceptedTypes.includes(file.type);
    },
    async getThumbnailDimensions(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new window.Image();
          img.onload = () => {
            resolve({ width: img.width, height: img.height });
          };
          img.onerror = reject;
          img.src = e.target.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    async checkUploadingThumbnailSize(file) {
      const dimensions = await this.getThumbnailDimensions(file);
      const aspectRatio = calculateAspectRatio(dimensions.width, dimensions.height);
      const selectedDimension = this.videoDimensionDropdownOption[this.selectedVideoDimension.value];
      return aspectRatio.equals(selectedDimension.aspectRatio);
    },
    handleClickOutsideRichVideoDropdowns(event) {
      const { videoDimensionDropdownRef, actionDropdownRef } = this.$refs;
      if (videoDimensionDropdownRef && !videoDimensionDropdownRef.contains(event.target)) {
        this.isVideoDimensionDropdownBoxClicked = false;
      }
      if (actionDropdownRef && !actionDropdownRef.contains(event.target)) {
        this.isActionDropdownBoxClicked = false;
      }
    },
    isURLValid(url) {
      const regex = /^(https?:\/\/).+/;
      return regex.test(url);
    },
  }
};
</script>

<style lang="scss" scoped>
.rich-video-message-container {
    margin-top: 20px;
    border: 1px solid #DADCE4;
    border-radius: 8px;
    p, label{
        margin-bottom: 0px;
    }
    .header-message{
        display: flex;
        justify-content: space-between;
        align-items: center;
        background-color: #F5F6FA;
        font-weight: 500;
        font-size: 14px;
        line-height: 24px;
        letter-spacing: 0px;
        color: #0D173C;
        padding: 10px 16px;
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
        border-bottom: 1px solid #DADCE4;
        i{
            font-size: 16px;
            color: #4B5178;
            cursor: pointer;
        }
    }
    .message-content{
        padding: 20px;
        &.video-container{
          padding-left: 0px;
        }
        .input-group{
            display: flex;
            flex-direction: column;
            padding-top: 8px;
        }
        .video-dimension-dropdown{
          position: relative;
        }
        .item-name-input{
            width: 100%;
            height: 40px;
            padding: 8px 16px;
            border-radius: 8px;
            border: 1px solid #DADCE4;
            font-size: 14px;
            line-height: 150%;
            letter-spacing: 0px;
            color: #0D173C;
        }
        select, input:focus-visible{
            outline: none;
        }
        .text-description{
            font-weight: 400;
            font-size: 12px;
            line-height: 150%;
            letter-spacing: 0px;
            color: #868BA6;
            margin-top: 4px;
        }
        .dropzone-container{
            #video-dropzone{
                width: 250px;
                height: 250px;
                border-radius: 8px;
                border: 1px dashed #8286FF;
                background-color: #EBEAFF;
                .dropzone-custom-input{
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    height: 100%;
                    i{
                        font-size: 40px;
                        color: #4848FF;
                        margin-bottom: 19px;
                    }
                    .dropzone-custom-title{
                        font-weight: 500;
                        font-size: 16px;
                        line-height: 120%;
                        letter-spacing: 0px;
                        text-align: center;
                        color: #4B5178;
                        margin-bottom: 24px;
                        .text-highlight{
                            color: #4848FF;
                        }
                    }
                    .text-subtitle{
                        font-weight: 400;
                        font-size: 12px;
                        line-height: 150%;
                        letter-spacing: 0px;
                        text-align: center;
                        color: #868BA6;
                    }
                }
            }
            #thumbnail-dropzone{
                width: 100px;
                height: 100px;
                min-height: 100px !important;
                padding: 0px;
                border-radius: 8px;
                border: 1px dashed #8286FF;
                background-color: #EBEAFF;
                .dropzone-custom-input{
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    height: 100%;
                    i{
                        font-size: 40px;
                        color: #4848FF;
                        // margin-bottom: 19px;
                    }
                    .dropzone-custom-title{
                        font-weight: 500;
                        font-size: 16px;
                        line-height: 120%;
                        letter-spacing: 0px;
                        text-align: center;
                        color: #4B5178;
                        margin-bottom: 24px;
                        .text-highlight{
                            color: #4848FF;
                        }
                    }
                    .text-subtitle{
                        font-weight: 400;
                        font-size: 12px;
                        line-height: 150%;
                        letter-spacing: 0px;
                        text-align: center;
                        color: #868BA6;
                    }
                }
            }
        }
        .thumbnail-container{
            width: 250px;
            height: 250px;
            border-radius: 8px;
            overflow: hidden;
            video{
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
            .thumbnail-remove{
                position: absolute;
                top: 10px;
                right: 10px;
                width: 30px;
                height: 30px;
                display: flex;
                justify-content: center;
                align-items: center;
                background-color: #0D173C7A;
                border-radius: 50%;
                cursor: pointer;
            }
        }
        .video-thumbnail-container{
          width: 100px;
          height: 100px;
          border-radius: 8px;
          overflow: hidden;
          img{
              width: 100%;
              height: 100%;
              object-fit: cover;
          }
          .thumbnail-remove{
              position: absolute;
              top: 10px;
              right: 10px;
              width: 30px;
              height: 30px;
              display: flex;
              justify-content: center;
              align-items: center;
              background-color: #0D173C7A;
              border-radius: 50%;
              cursor: pointer;
          }
        }
        .error-message{
            font-weight: 400;
            font-size: 12px;
            line-height: 150%;
            letter-spacing: 0px;
            color: #EF6767;
            height: 18px;
            width: max-content;
            padding-top: 10px;
        }
        .error-container{
          height: 28px;
        }
        .video-and-action-container{
            display: flex;
            justify-content: space-between;
            align-items: start;
            gap: 4px;
            .action-container{
                flex-grow: 1;
                .action-dropdown{
                  position: relative;
                }
            }
        }
    }
    .item-selected{
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 12px;
        border: 1px solid #DADCE4;
        border-radius: 8px;
        min-width: 130px;
        height: 40px;
        cursor: pointer;
        p{
            font-weight: 400;
            font-size: 14px;
            line-height: 150%;
            letter-spacing: 0px;
            color: #111B49;
        }
        i{
            color: #868BA6;
            font-size: 20px;
        }
    }
    .item-dropdown{
        background-color: #FFFFFF;
        border-radius: 8px;
        border: 1px solid #DADCE4;
        margin-top: 4px;
        padding: 8px 12px;
        min-width: 130px;
        width: 100%;
        position: absolute;
        z-index: 10;
        .item-option{
            padding: 8px 0px;
            cursor: pointer;
        }
        .item-option.selected-item{
            padding: 8px 0px;
            color: #2962DF;
        }
    }
    .input-label{
        font-weight: 500;
        font-size: 14px;
        line-height: 150%;
        letter-spacing: 0px;
        color: #0D173C;
        margin-bottom: 12px;
        label{
            font-weight: 500;
            font-size: 14px;
            line-height: 24px;
            letter-spacing: 0px;
            color: #4B5178;
        }
        i{
            font-size: 16px;
            color: #868BA6;
            margin-left: 5px;
            position: relative;
            top: 2px;
            cursor: pointer;
        }
        i:hover{
            color: #4848FF;
        }
        &.video-thumbnail{
            padding-top: 20px;
        }
        &.action-label{
            padding-top: 20px;
        }
    }
    .uploading-container{
        padding: 20px;
        .loading-section{
            display: flex;
            justify-content: center;
            align-items: center;
            width: 250px;
            height: 250px;
        }
    }
    .thumbnail-loading-container{
        .loading-section{
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100px;
            height: 100px;
        }
    }
}
</style>
